import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Container, Table } from 'reactstrap';
import dayjs from 'dayjs';

import { dateInputFieldFormat, formatHourMinutes, weeklyPlannerDayDisplayFormat } from '~libs/dayjs';
import { Stats } from '~weekly-planner/lib/common';

import { getStatistics } from '~appointments/actions/appointments';
import { getAll as getStatuses } from '~appointments/actions/statuses';

import { selectStatistics } from '~appointments/selectors/appointments';
import { selectAppointmentStatuses } from '~appointments/selectors/statuses';
import { selectDerivedWeekRange, selectShowSystemUsers } from '~weekly-planner/selectors';

interface ComponentProps {
  isOpen: boolean;
}

const Statistics: React.FC<ComponentProps> = ({ isOpen }) => {
  const dispatch = useAppDispatch();
  const stats = useAppSelector(selectStatistics) as Stats;
  const statuses = useAppSelector(selectAppointmentStatuses);
  const showSystemUsers = useAppSelector(selectShowSystemUsers);

  const derivedWeekRange = useAppSelector(selectDerivedWeekRange);
  const { week_start } = derivedWeekRange;

  const load = async () => {
    dispatch(getStatistics({ week_start, ...(showSystemUsers ? { show_system_users: true } : {}) }));
    dispatch(getStatuses());
  };

  useEffect(() => {
    if (isOpen) {
      load();
    }
  }, [isOpen, week_start, showSystemUsers]);

  const dayStats = stats?.daily || {};
  const statusStats = stats?.status || {};

  const displayValue = (value: number) => (value === null || value === 0 || isNaN(value) ? '-' : value);

  const formatTime = (avgMinutes: number) => {
    if (avgMinutes) return formatHourMinutes(avgMinutes);
    return '-';
  };

  const renderRow = (header: string, row: any, key: string) => (
    <tr key={key}>
      <th>{header}</th>
      <td>{displayValue(row?.count)}</td>
      <td>{displayValue(row?.hours)}</td>
      <td>{displayValue(row?.users)}</td>
      <td>{formatTime(row?.avg_duration)}</td>
    </tr>
  );

  const renderStatuses = () => (
    <tbody>
      {statuses.map((status) => {
        const statusValue = statusStats[status.id];

        return (
          <tr key={status.id}>
            <td>{status.name}</td>
            <td className="text-center">{displayValue(Number(statusValue))}%</td>
          </tr>
        );
      })}
    </tbody>
  );

  if (!week_start) return null;
  const startDate = dayjs(week_start, dateInputFieldFormat);
  const daysOfWeek = Array.from({ length: 7 }, (_, i) => startDate.add(i, 'day'));

  return (
    <div className="card-container">
      <Container className="inner-content limited">
        <Table size="sm">
          <thead>
            <tr>
              <th>Day</th>
              <th title="Total number of appointments">Count</th>
              <th title="Total appointment hours ">Hours</th>
              <th title="Care Workers available">CW</th>
              <th title="Mean average duration of appointments">Avg T</th>
            </tr>
          </thead>
          <tbody>
            {daysOfWeek.map((day) =>
              renderRow(
                day.format(weeklyPlannerDayDisplayFormat),
                dayStats[day.format(dateInputFieldFormat)],
                day.toISOString(),
              ),
            )}
            {renderRow('Totals', dayStats['total'], 'total')}
          </tbody>
        </Table>
        <Table striped>
          <thead>
            <tr>
              <th>Status</th>
              <th className="text-center">Percentage</th>
            </tr>
          </thead>
          {renderStatuses()}
        </Table>
      </Container>
    </div>
  );
};

export default Statistics;
